/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { type ReactNode, useEffect } from 'react';

import { ProductCard } from '@coursera/cds-core';
import type { DegreeUpsellType, ModalName, PathwaysMerchandisingModule } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

export type FormattedDegree = {
  name: string;
  slug: string;
  thumbnailUrl: string;
  id: string;
  cardFooter: ReactNode;
  partner: {
    name: string;
    logoUrl: string;
  };
};

const styles = {
  cardContainer: css`
    height: 100%;

    .cds-ProductCard-grid {
      min-width: unset !important;
    }
  `,
  listCardBorder: css`
    border-radius: var(--cds-border-radius-100);
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
  `,
  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset !important;
    }
  `,
};

export type TrackingData = {
  index: number;
  sourceId?: string;
  sourceSlug?: string;
  carouselId?: string;
  modalName?: ModalName;
  upsellType?: DegreeUpsellType;
  pathwaysMerchandisingModule?: PathwaysMerchandisingModule;
};

export type Props = {
  degree: FormattedDegree;
  openInNewTab?: boolean;
  cardImage?: JSX.Element;
  variant?: 'list' | 'grid';
  trackingData: TrackingData;
  disableCta?: boolean;
  isModalOpen?: boolean;
};

const DegreeCard = ({
  degree,
  openInNewTab,
  variant = 'list',
  cardImage,
  trackingData,
  disableCta,
  isModalOpen,
}: Props) => {
  const track = useTracker();

  const eventData = {
    productCard: { index: trackingData.index },
    product: { id: trackingData.sourceId || '' },
    pathwaysMerchandisingModule: trackingData.pathwaysMerchandisingModule,
    pathwaysUpsellTarget: { id: degree.id },
    pathwaysUpsellType: trackingData.upsellType,
    ...(trackingData.modalName ? { modal: { name: trackingData.modalName } } : {}),
    ...(trackingData.carouselId ? { carousel: { id: trackingData.carouselId } } : {}),
  };

  useEffect(() => {
    if (trackingData.sourceId && isModalOpen) {
      track('view_product_card', eventData);
    }
    // Only send this event on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const handleClick = () => {
    if (trackingData.sourceId) {
      track('click_product_card', eventData);
    }
  };

  return (
    <div css={[styles.cardContainer, variant === 'list' && styles.listCardContainer]}>
      <ProductCard
        variant={variant}
        productType="Degree"
        partners={[degree.partner]}
        renderPreviewImage={cardImage ? () => cardImage : undefined}
        title={{
          name: degree.name,
          linkProps: disableCta
            ? undefined
            : {
                href: `/degrees/${degree.slug}`,
                target: openInNewTab ? '_blank' : undefined,
                onClick: handleClick,
              },
        }}
        footer={degree.cardFooter}
        css={variant === 'list' && styles.listCardBorder}
        data-e2e="degree-card"
      />
    </div>
  );
};

export default DegreeCard;
